import { useState, useCallback, useEffect } from 'react';
import { 
  getAllOnboardings, 
  getOnboardingById, 
  createOnboarding, 
  updateOnboarding, 
  deleteOnboarding,
  Onboarding 
} from '@/services/onboarding.service';
import { useSharedDataStore } from '@/stores/sharedDataStore';

export interface UseOnboardingReturn {
  onboardings: Onboarding[];
  loading: boolean;
  error: string | null;
  fetchOnboardings: (params?: any) => Promise<void>;
  getOnboarding: (id: string) => Promise<Onboarding | null>;
  createOnboardingRecord: (data: FormData) => Promise<void>;
  updateOnboardingRecord: (id: string, data: Partial<Onboarding>) => Promise<void>;
  deleteOnboardingRecord: (id: string) => Promise<void>;
  updateOnboardingTasks: (onboardingId: string, taskIds: string[]) => Promise<void>;
  updateOnboardingTaskStatus: (onboardingId: string, taskInstanceId: string, status: string) => Promise<void>;
  calculateAndUpdateCompletion: (onboardingId: string) => Promise<void>;
  calculateCompletionForAllOnboardings: () => Promise<void>;
  clearError: () => void;
}

export const useOnboarding = (): UseOnboardingReturn => {
  const [onboardings, setOnboardings] = useState<Onboarding[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { onboardings: sharedOnboardings } = useSharedDataStore();

  // Use shared data when available
  useEffect(() => {
    if (sharedOnboardings.length > 0) {
      setOnboardings(sharedOnboardings);
    }
  }, [sharedOnboardings]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const fetchOnboardings = useCallback(async (params?: any) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllOnboardings(params);
      const onboardingData = response.data?.data || response.data || [];
      setOnboardings(Array.isArray(onboardingData) ? onboardingData : []);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch onboarding records';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const getOnboarding = useCallback(async (id: string): Promise<Onboarding | null> => {
    try {
      setLoading(true);
      setError(null);
      const response = await getOnboardingById(id);
      return response.data?.data || response.data || null;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch onboarding record';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createOnboardingRecord = useCallback(async (data: FormData) => {
    try {
      setLoading(true);
      setError(null);
      await createOnboarding(data);
      // Refresh the list after creating
      await fetchOnboardings();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create onboarding record';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchOnboardings]);

  const updateOnboardingRecord = useCallback(async (id: string, data: Partial<Onboarding>) => {
    try {
      setLoading(true);
      setError(null);
      await updateOnboarding(id, data);
      // Refresh the data from server to get the actual updated data
      await fetchOnboardings();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update onboarding record';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchOnboardings]);

  const deleteOnboardingRecord = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      await deleteOnboarding(id);
      // Remove from local state
      setOnboardings(prev => prev.filter(onboarding => onboarding._id !== id));
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete onboarding record';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateOnboardingTasks = useCallback(async (onboardingId: string, taskIds: string[]) => {
    try {
      setLoading(true);
      setError(null);
      const { updateOnboardingTasks: updateTasks } = await import('../services/onboarding.service');
      await updateTasks(onboardingId, taskIds);
      // Refresh the data from server to get the actual updated data
      await fetchOnboardings();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update onboarding tasks';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchOnboardings]);

  const updateOnboardingTaskStatus = useCallback(async (onboardingId: string, taskInstanceId: string, status: string) => {
    try {
      setLoading(true);
      setError(null);
      const { updateOnboardingTaskStatus: updateTaskStatus } = await import('../services/onboarding.service');
      await updateTaskStatus(onboardingId, taskInstanceId, status);
      // No need to refresh since optimistic updates handle the UI
      // The server response is already handled by the optimistic update
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update task status';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const calculateAndUpdateCompletion = useCallback(async (onboardingId: string) => {
    try {
      setLoading(true);
      setError(null);
      const { calculateAndUpdateCompletion: calculateCompletion } = await import('../services/onboarding.service');
      await calculateCompletion(onboardingId);
      // Refresh the data from server to get the actual updated data
      await fetchOnboardings();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to calculate completion percentage';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchOnboardings]);

  const calculateCompletionForAllOnboardings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { calculateCompletionForAllOnboardings: calculateAllCompletion } = await import('../services/onboarding.service');
      await calculateAllCompletion();
      // Refresh the data from server to get the actual updated data
      await fetchOnboardings();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to calculate completion for all onboardings';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchOnboardings]);

  return {
    onboardings,
    loading,
    error,
    fetchOnboardings,
    getOnboarding,
    createOnboardingRecord,
    updateOnboardingRecord,
    deleteOnboardingRecord,
    updateOnboardingTasks,
    updateOnboardingTaskStatus,
    calculateAndUpdateCompletion,
    calculateCompletionForAllOnboardings,
    clearError
  };
};
